// Wave 5: Admin > Invites > Waitlist. Everyone who joined the waitlist (landing page or a closed country's sign-up),
// newest first; pick some, choose their starting credits (0, 1,500 or 5,000) and "Invite selected" makes one invite
// link per person, locked to their email. The links are shown here to copy and send by hand: nothing is emailed.
// GET /api/admin/waitlist, POST /api/admin/waitlist/invite (api/src/waitlist-admin.ts).
import { useMemo, useState } from "react";
import { call } from "../api";
import { toast } from "../store";
import { ErrorNote, fmtCredits, Icon, Spinner, timeAgo, useLoad } from "./bits";

export type WaitlistEntry = {
  id: string;
  email: string;
  country: string | null;
  joinedAt: string;
  account: { id: string; access: string | null } | null;
  invite: { code: string; url: string; status: "open" | "used" | "revoked" | "expired" | null; credits: number; invitedAt: string | null } | null;
};
type Made = { id: string; email: string; code: string; url: string };

const isObj = (v: unknown): v is Record<string, unknown> => !!v && typeof v === "object" && !Array.isArray(v);
const str = (v: unknown) => (typeof v === "string" ? v : "");

export function normEntries(v: unknown): WaitlistEntry[] {
  const list = isObj(v) && Array.isArray(v.entries) ? v.entries : [];
  return list.filter(isObj).map((e) => {
    const inv = isObj(e.invite) ? e.invite : null;
    const acct = isObj(e.account) ? e.account : null;
    return {
      id: str(e.id),
      email: str(e.email),
      country: str(e.country) || null,
      joinedAt: str(e.joinedAt),
      account: acct ? { id: str(acct.id), access: str(acct.access) || null } : null,
      invite: inv ? { code: str(inv.code), url: str(inv.url), status: (["open", "used", "revoked", "expired"].includes(str(inv.status)) ? str(inv.status) : null) as "open" | null, credits: Number(inv.credits) || 0, invitedAt: str(inv.invitedAt) || null } : null,
    };
  });
}

/** Who can be invited now: no invite yet, or only a revoked / expired one, and no account with access already. */
export const invitable = (e: WaitlistEntry) => (!e.invite || e.invite.status === "revoked" || e.invite.status === "expired") && e.account?.access !== "active";

export const PRESETS = [0, 1500, 5000];

async function copy(text: string, what: string) {
  try {
    await navigator.clipboard.writeText(text);
    toast(`Copied ${what}.`, "good");
  } catch {
    window.prompt("Copy", text);
  }
}

export function WaitlistWaves({ onInvited }: { onInvited?: () => void }) {
  const { data, error, loading, reload } = useLoad(async () => normEntries(await call("GET", "admin/waitlist")), []);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [credits, setCredits] = useState(0);
  const [busy, setBusy] = useState(false);
  const [made, setMade] = useState<Made[] | null>(null);
  const open = useMemo(() => (data ?? []).filter(invitable), [data]);

  const toggle = (id: string) => {
    const next = new Set(picked);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setPicked(next);
  };
  const invite = async () => {
    setBusy(true);
    try {
      const r = await call("POST", "admin/waitlist/invite", { ids: [...picked], credits });
      const invites = (isObj(r) && Array.isArray(r.invites) ? r.invites : []).filter(isObj).map((i) => ({ id: str(i.id), email: str(i.email), code: str(i.code), url: str(i.url) }));
      setMade(invites);
      setPicked(new Set());
      toast(`${invites.length} invite link${invites.length === 1 ? "" : "s"} made. Nothing was emailed: copy and send them yourself.`, "good");
      reload();
      onInvited?.();
    } catch (err) {
      toast(`Couldn't make the invites. ${(err as Error).message}`, "bad");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="invite-list waitlist-waves" aria-labelledby="wl-h">
      <header className="section-head">
        <h2 id="wl-h">Waitlist</h2>
        {data && <span className="muted">{open.length} waiting · {data.length} in all</span>}
      </header>
      {error && <ErrorNote text={`Couldn't load the waitlist. ${error}`} retry={reload} />}
      {loading && !data && <Spinner label="Loading" />}
      {data && !data.length && <p className="muted">Nobody on the waitlist yet.</p>}

      {made && made.length > 0 && (
        <div className="invite-made" role="status">
          <p className="panel-label">New invite links (not emailed)</p>
          <ul className="wl-made">
            {made.map((m) => (
              <li key={m.code}>
                <span>{m.email}</span> <code>{m.url}</code>{" "}
                <button type="button" className="icon-btn" onClick={() => void copy(m.url, "the link")} aria-label={`Copy the invite link for ${m.email}`}>
                  <Icon name="copy" size={16} />
                </button>
              </li>
            ))}
          </ul>
          <button type="button" className="btn btn-quiet btn-small" onClick={() => void copy(made.map((m) => `${m.email}\t${m.url}`).join("\n"), "every link")}>
            <Icon name="copy" size={16} /> Copy all (email, link)
          </button>
        </div>
      )}

      {!!data?.length && (
        <>
          <div className="wl-bar">
            <button type="button" className="btn btn-quiet btn-small" onClick={() => setPicked(picked.size === open.length ? new Set() : new Set(open.map((e) => e.id)))} disabled={!open.length}>
              {picked.size === open.length && open.length ? "Select none" : "Select everyone waiting"}
            </button>
            <span className="wl-presets" role="radiogroup" aria-label="Starting credits">
              {PRESETS.map((n) => (
                <button key={n} type="button" role="radio" aria-checked={credits === n} className={`btn btn-small ${credits === n ? "btn-primary" : "btn-quiet"}`} onClick={() => setCredits(n)}>
                  {fmtCredits(n)} credits
                </button>
              ))}
            </span>
            <button type="button" className="btn btn-primary btn-small" data-action="waitlist.invite" disabled={busy || !picked.size} onClick={() => void invite()}>
              {busy ? "Making links" : `Invite selected (${picked.size})`}
            </button>
          </div>
          <ul className="inv-rows">
            {data.map((e) => {
              const can = invitable(e);
              return (
                <li key={e.id} className="inv-row">
                  <label className="inv-main wl-pick">
                    <input type="checkbox" disabled={!can} checked={picked.has(e.id)} onChange={() => toggle(e.id)} aria-label={`Pick ${e.email}`} />{" "}
                    <span>
                      <span className="inv-who">{e.email}</span>
                      <span className="inv-meta">
                        {e.country ?? "unknown country"} · joined {timeAgo(e.joinedAt)}
                        {e.invite ? ` · invite ${e.invite.status} (${fmtCredits(e.invite.credits)} credits)` : ""}
                        {e.account?.access === "active" ? " · has an account" : ""}
                      </span>
                    </span>
                  </label>
                  {e.invite?.status === "open" && (
                    <button type="button" className="icon-btn" onClick={() => void copy(e.invite!.url, "the link")} aria-label={`Copy the invite link for ${e.email}`} title="Copy link">
                      <Icon name="copy" size={18} />
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        </>
      )}
    </section>
  );
}
